import { useMaskito } from '@maskito/react';
import { Amount } from '@ui/components/Amount/Amount';
import { NoSSR } from '@ui/components/NoSSR';
import { USD_DECIMALS } from '@ui/constants/numbersDecimals';
import { createCollateralInputMask } from '@ui/lib/maskito';
import { Portal } from '@ui/lib/park/portal';
import { Field } from '@ui/lib/park/ui/field';
import { getBalanceAfterGas } from '@ui/utils/getBalanceAfterGas';
import BigNumber from 'bignumber.js';
import { debounce, isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, styled } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import { LeverageSetter } from '../TradeModule/LeverageSetter';
import { useIsLong } from '../TradeModule/store/useIsLong';
import { CollateralToken } from './CollateralToken';
import { CollateralValueEst } from './CollateralValueEst';
import { Error } from './Error';
import { CollateralInputProps } from './types';

export const CollateralInput = ({
  onInput,
  tokenPrice,
  value,
  setToken,
  token,
  tokenBalance,
  getErrorMessage,
  leverageSetterEnabled = false,
  gasFee,
  overridePayableTokens,
  balanceLabelPrefix,
  ...p
}: CollateralInputProps) => {
  const { t } = useTranslation();
  const isLong = useIsLong((s) => s._persistValue);
  const errPortalRef = useRef<HTMLDivElement>(null);

  const [isInputToken, setIsInputToken] = useState<boolean>(true);

  const [localValue, setLocalValue] = useState<string>(() =>
    isInputToken
      ? value?.token?.toString() ?? ''
      : value?.usd?.toString() ?? '',
  );

  const balance = useMemo(() => {
    if (!tokenBalance || !tokenPrice) return undefined;

    const usdBalance = tokenBalance?.times(tokenPrice || 0);

    return isInputToken ? tokenBalance : usdBalance;
  }, [isInputToken, tokenBalance?.toString(), tokenPrice?.toString()]);

  const maxInputValue = useMemo(() => {
    const gas = isInputToken ? gasFee?.gasInNativeToken : gasFee?.gasInUsd;

    return getBalanceAfterGas({
      balance,
      gasFee: gas,
      isNative: token?.isNative,
    });
  }, [
    balance,
    gasFee?.gasInNativeToken?.toString(),
    gasFee?.gasInUsd?.toString(),
    isInputToken,
    token?.isNative,
  ]);

  const decimals = isInputToken ? token?.decimals : USD_DECIMALS;

  const inputRefCallback = useMaskito({
    options: createCollateralInputMask({
      precision: decimals,
    }),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceInput = useCallback(
    debounce((nextValue: string) => {
      if (isInputToken) {
        const token = nextValue ? new BigNumber(nextValue) : null;
        onInput?.({
          token,
          usd: tokenPrice && token ? token.times(tokenPrice) : null,
        });
      } else {
        const usd = nextValue ? new BigNumber(nextValue) : null;
        onInput?.({
          usd,
          token: tokenPrice && usd ? usd.div(tokenPrice) : null,
        });
      }
    }, 100),
    [isInputToken, onInput, tokenPrice?.toNumber()],
  );

  const handleValueChange = (v: string) => {
    setLocalValue(v);
    const nextValue = v;
    debounceInput(nextValue);
  };

  const errorMessage = getErrorMessage(isInputToken);

  const { isConnected } = useAccount();

  // to handle 100% slider value, we cannot pass Number into the slider props
  // because there will always be a rounding error
  // Instead, we pass percentage value, and calculate the actual value from balance
  const handleSliderChange = (percentage: number) => {
    if (!maxInputValue) return;
    const bignum = new BigNumber(maxInputValue);
    const value = bignum.times(percentage).div(100);
    // BigNumber.toFixed gives the full precision
    decimals && setLocalValue(value.toFixed(decimals));
    debounceInput(value.toFixed());
  };

  useEffect(() => {
    if (value.token && value.usd && tokenPrice) {
      debounceInput(
        isInputToken ? value.token.toString() : value.usd.toString(),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenPrice?.toString(), token?.symbol]);

  return (
    <>
      <Field.Root
        gap="12px"
        // V2: the field is flush on the panel — only the amount cell is boxed
        bg="transparent"
        p="0"
        invalid={!!errorMessage}
        // invalid must only tint the amount cell below — neutralize the
        // recipe's root-level red outline
        css={{
          '&[data-invalid]': {
            borderColor: 'transparent',
            _hover: { borderColor: 'transparent' },
            _focusWithin: {
              borderColor: 'transparent',
              _hover: { borderColor: 'transparent' },
            },
          },
        }}
        {...p}
        data-cy="CollateralInputRoot"
        data-cy-is-input-token={isInputToken}
      >
        <Flex justifyContent="space-between">
          <Field.Context>
            {(context) => (
              <Field.Label
                textStyle="v2.sectionLabel"
                color={context.invalid ? 'v2.short.default' : 'v2.text.subtle'}
              >
                {t('common.Collateral')} ({token?.displaySymbol})
              </Field.Label>
            )}
          </Field.Context>

          <Field.Label
            alignItems="center"
            display="flex"
            gap="4px"
            textStyle="v2.meta"
            color="v2.text.muted"
          >
            <Amount
              textStyle="numbers.xsmall"
              value={isConnected ? balance : undefined}
              _hover={{
                cursor: 'pointer',
              }}
              opts={{
                prefix: balanceLabelPrefix ?? `${t('common.Available')}: `,
                saveDecimalsMode: true,
                // floor so the label never shows more than what's actually
                // available (e.g. 99.9999 -> 99.99, not 100.00). Clicking still
                // fills the full-precision value.
                roundingMode: BigNumber.ROUND_DOWN,
              }}
              onClick={() => {
                handleSliderChange(100);
              }}
              textDecoration={
                balance && !balance.isZero() ? 'underline' : 'none'
              }
              data-cy="TokenBalance"
              data-cy-value={balance}
            />
            {/* {isConnected && !isNil(token) && !isNil(balance) && (
              <PlusButton token={token} balance={balance} />
            )} */}
          </Field.Label>
        </Flex>

        <Field.Context>
          {(context) => (
        <Flex
          bg="v2.bg.surface1"
          border="1px solid"
          borderColor={
            context.invalid ? 'v2.short.border' : 'v2.border.default'
          }
          borderRadius="4px"
          alignItems="center"
          gap="0px"
          pr="4px"
          justifyContent="space-between"
          _focusWithin={{
            borderColor: context.invalid
              ? 'v2.short.border'
              : 'v2.border.focus',
          }}
          transition="border-color 0.15s"
        >
          <Box flex={1} gap={0}>
            <Flex
              alignItems="center"
              justifyContent="flex-start"
              pl={!isInputToken ? '10px' : '0'}
              borderRadius="3px 0 0 3px"
            >
              {!isInputToken && (
                <Box fontSize={14} color="v2.text.muted">
                  {'$'}
                </Box>
              )}
              <Field.Input
                autoComplete="off"
                placeholder="0.0"
                fontFamily="Inter"
                fontSize="16px"
                fontWeight={500}
                style={{ fontVariantNumeric: 'tabular-nums' }}
                w="full"
                caretColor={isLong ? 'price.priceUp' : 'price.priceDown'}
                value={localValue}
                onInput={(e) => {
                  handleValueChange(e.currentTarget.value);
                }}
                // ml="-6px"
                padding="7px 10px"
                pl={!isInputToken ? '2px' : '10px'}
                bg="transparent"
                border="none"
                outline="none"
                // borderRadius="4px"
                _hover={{
                  bg: 'transparent',
                }}
                _focus={{
                  bg: 'transparent',
                  boxShadow: 'none',
                  outline: 'none',
                }}
                type="text"
                inputMode="decimal"
                ref={inputRefCallback}
                data-cy="CollateralInput"
              />
            </Flex>
          </Box>
          {isConnected && balance && !balance.isZero() && (
            <styled.button
              type="button"
              flexShrink={0}
              textStyle="v2.meta"
              fontWeight={500}
              color="v2.long.default"
              px="6px"
              mr="6px"
              cursor="pointer"
              _hover={{ color: 'v2.long.strong' }}
              onClick={() => handleSliderChange(100)}
              data-cy="CollateralMaxButton"
            >
              {t('common.MAX', 'MAX')}
            </styled.button>
          )}
          <NoSSR>
            <Flex alignItems="center" alignSelf="center" flexShrink={0}>
              <CollateralToken
                setToken={setToken}
                token={token}
                overridePayableTokens={overridePayableTokens}
              />
            </Flex>
          </NoSSR>
        </Flex>
          )}
        </Field.Context>
        <CollateralValueEst
          isInputToken={isInputToken}
          setIsInputToken={setIsInputToken}
          setLocalValue={setLocalValue}
          value={value}
          tokenSymbol={token?.symbol}
        />
        {leverageSetterEnabled && <LeverageSetter />}
        <Portal container={errPortalRef}>
          <Error msg={errorMessage || ''} />
        </Portal>
      </Field.Root>
      <Box ref={errPortalRef} />
    </>
  );
};
